import type { FC, SyntheticEvent } from 'react';
import {
  getAccessibleTabIndex,
  getAriaButtonRoleAttribute,
  handleAccessibleButtonKeyDown,
} from '../Button/ariaHelpers';
import { Icon } from '../Icon/Icon';
import { useConditionalClickHandler } from '../hooks/useConditionalClickHandler';
import { getChipClassNames } from './Chip.styles';
import { type ChipProps, fromChipToIconSize } from './Chip.types';

export const Chip: FC<ChipProps> = ({
  variant,
  selected,
  size = 'default',
  className,
  dataTestId,
  onClick,
  onRemove,
  children,
  loading,
  icon,
  iconStyle = 'regular',
  disabled,
  ...restProps
}) => {
  const interactive = onClick !== undefined;
  const DynamicComponent = interactive ? 'button' : 'div';

  const handleClick = useConditionalClickHandler<
    HTMLButtonElement | HTMLDivElement
  >(disabled, onClick);

  const handleRemoveClick = (e: SyntheticEvent) => {
    // Prevent the click event from bubbling up to the chip's onClick handler
    e.stopPropagation();

    onRemove?.();
  };

  let chipIcon = <></>;

  if (icon) {
    chipIcon = (
      <Icon
        glyph={icon}
        loading={loading}
        size={fromChipToIconSize[size]}
        iconStyle={iconStyle}
      />
    );
  }

  return (
    <DynamicComponent
      type={DynamicComponent === 'button' ? 'button' : undefined}
      {...restProps}
      data-testid={dataTestId}
      className={getChipClassNames(
        variant,
        size,
        selected,
        disabled,
        interactive,
        className,
      )}
      onClick={handleClick}
      {...getAriaButtonRoleAttribute(interactive)}
      tabIndex={getAccessibleTabIndex(!interactive || disabled)}
      onKeyDown={(e) => {
        if (!interactive) {
          handleAccessibleButtonKeyDown(e, disabled);
        }
      }}
    >
      {chipIcon}
      {children !== undefined && <div className='truncate'>{children}</div>}
      {onRemove && (
        <Icon
          className={
            disabled
              ? 'text-status-disabled-dark'
              : 'cursor-pointer text-default hover:text-status-error'
          }
          iconStyle='duotone'
          glyph='fa-circle-xmark'
          size={fromChipToIconSize[size]}
          onClick={!disabled ? handleRemoveClick : undefined}
          data-testid='remove-icon'
        />
      )}
    </DynamicComponent>
  );
};
